'use client'

import React, { useCallback, useState } from 'react'
import { cn } from '@scalis/utils'
import { useCSVExportContext } from './csv-export-context'
import { CSVExportProgressToast } from './csv-export-progress-toast'

interface CSVExportToastStackProps {
  /** Maximum number of toasts to show simultaneously */
  maxToasts?: number
  /** Auto-hide completed exports after this many seconds (0 = never) - DEPRECATED: Now keeping until user action */
  autoHideCompleted?: number
  /** Position of the toast stack */
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
}

export const CSVExportToastStack: React.FC<CSVExportToastStackProps> = ({
  maxToasts = 3,
  autoHideCompleted = 0, // Disabled - keep until user action
  position = 'bottom-left',
}) => {
  const [isClient, setIsClient] = React.useState(false)
  const { activeExports, cancelExport, downloadExport, dismissExport } = useCSVExportContext()

  // Only render on client to prevent hydration issues
  React.useEffect(() => {
    setIsClient(true)
  }, [])

  // Track minimized state for each export
  const [minimizedExports, setMinimizedExports] = useState<Set<string>>(new Set())

  // Track dismissed/closed exports (user clicked X button)
  const [dismissedExports, setDismissedExports] = useState<Set<string>>(new Set())

  // Filter and sort exports for display
  const visibleExports = React.useMemo(() => {
    // Filter out dismissed exports first
    const nonDismissed = activeExports.filter((exp) => !dismissedExports.has(exp.id))

    // Sort by creation time (newest first) and limit display
    const sorted = nonDismissed.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()).slice(0, maxToasts)

    return sorted
  }, [activeExports, dismissedExports, maxToasts])

  // Auto-hide completed exports
  React.useEffect(() => {
    if (autoHideCompleted <= 0) return

    visibleExports.forEach((exportRecord) => {
      if (exportRecord.status.state === 'completed') {
        const timeoutId = setTimeout(() => {
          setMinimizedExports((prev) => new Set(Array.from(prev).concat(exportRecord.id)))
        }, autoHideCompleted * 1000)

        return () => clearTimeout(timeoutId)
      }
    })
  }, [visibleExports, autoHideCompleted])

  const handleMinimize = useCallback((exportId: string) => {
    setMinimizedExports((prev) => {
      const newSet = new Set(Array.from(prev).concat(exportId))
      return newSet
    })
  }, [])

  const handleExpand = useCallback((exportId: string) => {
    setMinimizedExports((prev) => {
      const newSet = new Set(prev)
      newSet.delete(exportId)
      return newSet
    })
  }, [])

  const handleCancel = useCallback(
    async (exportId: string) => {
      try {
        await cancelExport(exportId)
        // Also remove from minimized state
        setMinimizedExports((prev) => {
          const newSet = new Set(prev)
          newSet.delete(exportId)
          return newSet
        })
        // Add to dismissed so it disappears immediately
        setDismissedExports((prev) => new Set(Array.from(prev).concat(exportId)))
      } catch {
        // Silently handle errors
      }
    },
    [cancelExport],
  )

  const handleDismiss = useCallback(
    async (exportId: string) => {
      try {
        // Persistently dismiss the export in IndexedDB
        await dismissExport(exportId)

        // Also update local state for immediate UI response
        setDismissedExports((prev) => new Set(Array.from(prev).concat(exportId)))
        setMinimizedExports((prev) => {
          const newSet = new Set(prev)
          newSet.delete(exportId)
          return newSet
        })
      } catch {
        // Silently handle errors
      }
    },
    [dismissExport],
  )

  const handleDownload = useCallback(
    async (exportId: string) => {
      try {
        await downloadExport(exportId)

        // Give user 15 seconds to see the success state before auto-dismissing
        setTimeout(() => {
          handleDismiss(exportId)
        }, 15000)
      } catch {
        // Silently handle errors
      }
    },
    [downloadExport, handleDismiss],
  )

  // Get positioning classes for the viewport - using higher z-index to avoid conflicts with other toast systems
  const getViewportClasses = () => {
    switch (position) {
      case 'bottom-left':
        return 'fixed bottom-4 left-4 z-[200] flex max-h-screen flex-col-reverse max-w-[420px] pointer-events-none'
      case 'bottom-right':
        return 'fixed bottom-4 right-4 z-[200] flex max-h-screen flex-col-reverse max-w-[420px] pointer-events-none'
      case 'top-left':
        return 'fixed top-4 left-4 z-[200] flex max-h-screen flex-col max-w-[420px] pointer-events-none'
      case 'top-right':
        return 'fixed top-4 right-4 z-[200] flex max-h-screen flex-col max-w-[420px] pointer-events-none'
      default:
        return 'fixed bottom-4 right-4 z-[200] flex max-h-screen flex-col-reverse max-w-[420px] pointer-events-none'
    }
  }

  // Don't render during SSR to prevent hydration issues
  if (!isClient || visibleExports.length === 0) {
    return null
  }

  // Rendering CSV export toasts

  return (
    <div className={getViewportClasses()}>
      {visibleExports.map((exportRecord) => {
        const isMinimized = minimizedExports.has(exportRecord.id)

        return (
          <div
            key={exportRecord.id}
            className={cn(
              'pointer-events-auto flex w-full overflow-hidden rounded-xl border border-neutral-00 bg-white shadow-lg',
              'mb-3', // Add spacing between toasts
              'transition-all duration-300 ease-in-out',
              isMinimized ? 'h-16' : 'min-h-[120px]', // Different heights for minimized vs expanded
            )}
          >
            <CSVExportProgressToast
              export={exportRecord}
              isMinimized={isMinimized}
              onMinimize={() => handleMinimize(exportRecord.id)}
              onExpand={() => handleExpand(exportRecord.id)}
              onCancel={handleCancel}
              onDismiss={handleDismiss}
              onDownload={handleDownload}
            />
          </div>
        )
      })}
    </div>
  )
}

export default CSVExportToastStack
